import * as API from '@user/api'; /*eslint-disable-line */
import { UserError } from '@shared/error/userError'; /*eslint-disable-line */
import { saveAs } from 'file-saver';
import { Module } from '@user/entities/module';
import { Profile } from '@user/entities/profile';
import { actionDebug as debug } from '@user/util/debuggers';

import { ModuleContainer } from '@user/entities/moduleContainer';
import { StorageState } from './storageState';

export const createActions = (router) => {
  const actions = {
    /* Appearance */
    setVariables(context, variables) {
      debug('setVariables', variables);
      context.commit('SET_VARIABLES', variables);
    },
    setOverlay(context, value) {
      debug('setOverlay', value);
      context.commit('SET_OVERLAY', value);
    },
    setLogo(context, type) {
      debug('setLogo', type);
      context.commit('SET_LOGO', type);
    },
    setProgressVisibility(context, visibility) {
      debug('setProgressVisibility', visibility);
      context.commit('SET_PROGRESS_VISIBILITY', visibility);
    },
    setNavVisibility(context, visibility) {
      debug('setNavVisibility', visibility);
      context.commit('SET_NAV_VISIBILITY', visibility);
    },
    setLayout(context, layoutName) {
      debug('setLayout', layoutName);
      context.commit('SET_LAYOUT', layoutName);
    },
    showSide(context, visibility) {
      debug('showSide', visibility);
      context.commit('SHOW_SIDE', !context.state.isOnSite && visibility);
    },
    /* END Appearance */

    async setProjectConfig(context, config) {
      debug('setProjectConfig', config);
      context.commit('SET_PROJECT_CONFIG', config);
    },

    async initLanguage(context) {
      debug('initLanguage');
      const { config } = context.state;
      if (!config.language || !config.language.length) {
        throw new Error('Project has no language');
      }
      const projectHasOneLanguage = config.language.length === 1;
      const languageQuery = router.currentRoute.query.langId;
      debug('initLanguage', languageQuery);
      if (languageQuery) {
        const languageQueryNotInProject = !config.language.find(
          (language) => language.id === languageQuery
        );
        if (languageQueryNotInProject) {
          debug('initLanguage', `Language ${languageQuery} not in project.`);
          const error = new Error('URL Error');
          error.code = 'E02';
          throw error;
        }
      }
      if (projectHasOneLanguage || !languageQuery) {
        context.dispatch('setLanguage', config.language[0].id);
      } else {
        context.dispatch('setLanguage', languageQuery);
      }
      await context.dispatch('loadDataProtectionStatement');
    },

    setProjectVariables(context, variables) {
      debug('setProjectVariables ', variables);
      context.commit('SET_PROJECT_VARIABLES', variables);
    },

    setLanguage(context, language) {
      debug('setLanguage', language);
      context.commit('SET_LANGUAGE', language);
    },
    setError(context, error) {
      debug('setError');
      context.commit('SET_ERROR', new UserError(error, context.state.language));
    },
    setResponse(context, response) {
      debug('setResponse');
      context.commit('SET_RESPONSE', response);
    },

    hideError(context) {
      debug('hideError');
      context.commit('HIDE_ERROR');
    },

    setCSTM(context, cstm) {
      debug('setCSTM');
      context.commit('SET_CSTM', cstm);
    },

    setDeviceType(context) {
      debug('setDeviceType');
      let deviceType = 'desktop';
      const ua = navigator.userAgent;

      if (/(tablet|ipad|playbook|silk)|(android(?!.*mobi))/i.test(ua)) {
        // Tablets -> mobile
        deviceType = 'mobile';
        // eslint-disable-next-line
      } else if (
        /Mobile|Android|iP(hone|od)|IEMobile|BlackBerry|Kindle|Silk-Accelerated|(hpw|web)OS|Opera M(obi|ini)/i.test(
          ua
        )
      ) {
        deviceType = 'mobile';
      }
      context.commit('SET_DEVICE_TYPE', deviceType);
    },

    async loadDataProtectionStatement(context) {
      debug('loadDataProtectionStatement');
      const options = {
        language: context.state.language,
        cstm: context.state.cstm,
      };
      const { response, error } = await API.fetchDataProtStatement(options);
      if (error || (!response || !response.data || !response.data.statement)) {
        context.dispatch('setError', { error, code: 'DP01' });
        return;
      }

      const statement = {
        texts: JSON.parse(response.data.statement),
        id: response.data.dpsm_dpsm_id,
        languageMapId: response.data.dplm_id,
        revision: response.data.revision,
        accepted: false,
      };
      context.commit('SET_DATA_PROTECTION_STATEMENT', statement);
    },
    acceptDataProtectionStatement(context, value) {
      debug('acceptDataProtectionStatement', value);
      context.commit('SET_DATA_PROTECTION_STATEMENT_ACCEPTED', value);
    },

    setDataProtectionModalVisibility(context, value) {
      debug('setDataProtectionModalVisibility', value);
      context.commit('SET_DATA_PROTECTION_STATEMENT_MODAL_VISIBILITY', value);
    },

    acceptContactingAgreement(context, value) {
      debug('acceptContactingAgreement', value);
      context.commit('SET_CONTACTING_STATEMENT_ACCEPTED', value);
    },

    async fetchUserData(context) {
      debug('fetchUserData');
      const res = await API.loadUserData(); // get userdata, TODO: rename
      context.commit('SET_LANGUAGE', res.language);
      context.commit('SET_ALL_USERDATA', res);
    },

    // TODO: is this redundant with fetchData?
    async fetchTasks(context) {
      debug('fetchTasks');
      context.dispatch('checkLoggedIn');
      if (!context.state.isLoggedIn) {
        return;
      }
      if (context.state.activeProfile === null) {
        try {
          await context.dispatch('loadProfile');
          if (context.state.activeProfile === null) {
            return;
          }
        } catch (error) {
          console.log(error);
          return;
        }
      }
      debug('fetchTasks, activeProfile is ', context.state.activeProfile);
      const res = await API.loadData(context.state.activeProfile.id);
      debug('fetchTasks, response is ', res);
      context.commit('SET_LANGUAGE', res.language);

      const modules = res.elements.map((el) => new Module(el));
      context.commit('SET_MODULES', modules);
      const finishedModules = res.finishedElements.map((el) => new Module(el));
      context.commit('SET_FINISHED_MODULES', finishedModules);
      context.dispatch('setProgress');
    },

    async fetchModuleContainers(context, profileId) {
      debug('fetchModuleContainers, profileId: ', profileId);
      context.dispatch('checkLoggedIn');
      if (!context.state.isLoggedIn) {
        return;
      }
      if (context.state.activeProfile === null) {
        try {
          await context.dispatch('loadProfile');
          if (context.state.activeProfile === null) {
            return;
          }
        } catch (error) {
          console.log(error);
          return;
        }
      }

      let res;
      try {
        res = await API.loadModuleContainers(context.state.activeProfile.id);
      } catch {
        context.commit('SET_MODULE_CONTAINERS', []);
        throw new Error('Could not load module containers');
      }

      debug('fetchModuleContainers, response is ', res);
      const modules = [];
      const finishedModules = [];

      // Baue moduleContainer aus API-Response
      const moduleContainers = res.containers.map((container) => {
        debug('fetchModuleContainers, build elements: ', container);

        // Wandle API-Response in Module um
        const containerElements = container.activeComponents.map(
          (el) => new Module(el)
        );
        const finishedContainerElements = container.finishedComponents.map(
          (el) => new Module(el)
        );

        debug('fetchModuleContainers, containerElements: ', containerElements);
        debug(
          'fetchModuleContainers, finishedElements: ',
          finishedContainerElements
        );

        modules.push(...containerElements);
        finishedModules.push(...finishedContainerElements);
        let iconPath;
        if (containerElements.length > 0) {
          iconPath = containerElements[0].imagePath;
        } else if (finishedContainerElements.length > 0) {
          iconPath = finishedContainerElements[0].imagePath;
        }
        debug('fetchModuleContainers', iconPath);
        const containerData = {
          id: container.pcc_id,
          name: container.display_name,
          description: container.description,
          iconPath: iconPath || 'IconDiagrammanalyse',
          size: container.duration || 8,
          type: container.type || 'test',
          finishedElements: finishedContainerElements,
          elements: containerElements,
        };

        try {
          // Review dev-808: could not init-Error im catch werfen, faultyContainers-Logik entferne?
          return new ModuleContainer(containerData);
        } catch (e) {
          // Gebe im Fehlerfall spezielles Object zurück
          return { id: containerData.id, name: container.name, error: e };
        }
      });
      debug('fetchModuleContainers, moduleContainers: ', moduleContainers);
      const faultyContainers = moduleContainers.filter((container) => {
        return Object.keys(container).length === 3 ? container : false;
      });
      debug('fetchModuleContainers, faultyContainers: ', faultyContainers);

      if (faultyContainers.length !== 0) {
        const filteredModuleContainers = moduleContainers.filter(
          (container) => Object.keys(container).length > 3
        );
        context.commit('SET_MODULE_CONTAINERS', filteredModuleContainers);
        throw new Error(
          `Could not initialize Containers: ${JSON.stringify(faultyContainers)}`
        );
      }

      debug('fetchModuleContainers, containers', moduleContainers);

      context.commit('SET_MODULE_CONTAINERS', moduleContainers);
      if (
        context.state.activeModuleContainer &&
        moduleContainers.find(
          (container) => container.id === context.state.activeModuleContainer.id
        )
      ) {
        const newContainer = moduleContainers.find(
          (container) => container.id === context.state.activeModuleContainer.id
        );
        context.commit('SET_ACTIVE_MODULE_CONTAINER', null);
        context.commit('SET_ACTIVE_MODULE_CONTAINER', newContainer);
      }
      context.dispatch('setProgress');
    },

    async fetchData(context) {
      debug('fetchData');
      context.dispatch('checkLoggedIn');
      if (!context.state.isLoggedIn) {
        return;
      }
      if (context.state.activeProfile === null) {
        try {
          await context.dispatch('loadProfile');
          if (context.state.activeProfile === null) {
            return;
          }
        } catch (error) {
          console.log(error);
          return;
        }
      }
      debug(`fetchData, activeProfile is ${context.state.activeProfile}`);
      const res = await API.loadData(context.state.activeProfile.id);
      context.commit('SET_LANGUAGE', res.language);
      context.commit('SET_ALL_USERDATA', res.userInfo);

      const modules = res.elements.map((el) => new Module(el));
      context.commit('SET_MODULES', modules);

      const finishedModules = res.finishedElements.map((el) => new Module(el));
      context.commit('SET_FINISHED_MODULES', finishedModules);
    },

    setActiveModule(context, module) {
      debug('setActiveModule', module);
      context.commit('SET_ACTIVE_MODULE', module);
    },

    setActiveModuleContainer(context, container) {
      debug('setActiveModuleContainer', container);
      context.commit('SET_ACTIVE_MODULE_CONTAINER', container);
    },

    async requestPicatKey(context, prcmId) {
      debug('requestPicatKey', prcmId);
      const profileId = context.state.activeProfile.id;
      if (!profileId) {
        context.dispatch('loadProfile');
        return;
      }
      const sessionURL = await API.requestSessionURL(prcmId, profileId);
      if (!!sessionURL && sessionURL.length === 0) {
        const picatInitError = new Error('could not initialize session');
        picatInitError.code = 'API03';
        throw picatInitError;
      }
      context.commit('SET_SESSION_URL', sessionURL);
    },

    async finishActiveModule(context) {
      debug('finishActiveModule: ', context.state.activeModule);
      if (context.state.activeModule.constructor === Array) {
        // Idee: neue API Schnittstelle die FBO Module abschließt
        const moduleIds = context.state.activeModule.map((module) => module.id);
        await API.setFBOModulesFinished(moduleIds);
      } else {
        await API.setModuleFinished(context.state.activeModule.id);
      }

      context.commit('SET_ACTIVE_MODULE', null);
      context.commit('SET_SESSION_URL', null);
    },

    async finishModule(context, prcmId) {
      debug('finishModule', prcmId);
      await API.setModuleFinished(prcmId);
      await context.dispatch('fetchData');
      context.dispatch('setProgress');
    },

    setTransitionStatus(context, inTransition) {
      debug('setTransitionStatus', inTransition);
      context.commit('SET_TRANSITION_STATUS', inTransition);
    },
    /**
     * Forms
     */
    async finishActiveModuleOnClient(context) {
      debug('finishActiveModuleOnClient');
      context.commit('SET_ACTIVE_MODULE', null);
      context.commit('SET_SESSION_URL', null);
      await context.dispatch('fetchTasks');
      context.dispatch('setProgress');
    },

    async finishActiveModuleContainer(context) {
      debug('finishActiveModuleContainer');

      context.commit('SET_ACTIVE_MODULE_CONTAINER', null);
    },

    async finishActiveModuleContainerAndFetch(context) {
      await context.dispatch('finishActiveModuleContainer');
      await context.dispatch(
        'fetchModuleContainers',
        context.state.activeProfile.id
      );
    },

    progressActiveModuleContainer(context) {
      debug(
        'progressActiveModuleContainer: ',
        context.state.activeModuleContainer
      );
      context.commit('PROGRESS_ACTIVE_MODULE_CONTAINER');
      context.commit(
        'SET_ACTIVE_MODULE',
        context.state.activeModuleContainer.activeElement
      );
    },

    setProgress(context) {
      debug('setProgress');
      let progress;
      if (context.state.moduleContainers.length === 0) {
        progress =
          (context.state.finishedModules.length /
            (context.state.modules.length +
              context.state.finishedModules.length)) *
          100;
      } else {
        progress =
          (context.getters.getFinishedModuleContainers.length /
            context.state.moduleContainers.length) *
          100;
      }
      // Falls der User schnell zurücknavigiert, wird activeProfile auf null gesetzt und die mutation schlägt fehl
      if (
        typeof context.state.activeProfile !== 'undefined' &&
        context.state.activeProfile !== null
      ) {
        context.commit('SET_PROGRESS', progress);
      }
    },

    setUserData(context, { key, value }) {
      debug('setUserData', key, value);
      context.commit('SET_USER_DATA', { key, value });
    },

    async saveProfile(context, data) {
      debug('saveProfile');
      const { error, response } = await API.saveProfile(API.urls.profile, data);
      if (error) {
        context.dispatch('setError', { error, code: 'API01' });
      }
      context.dispatch('setResponse', response);
      context.dispatch('loadProfile'); // TODO: Why load after save?
    },

    async setProfileOnSite(context, profileId) {
      debug(`setProfileOnSite: profileId is set to ${profileId}`);
      context.dispatch('setActiveProfile', parseInt(profileId, 10));
    },
    async loadProfile(context) {
      debug('loadProfile');
      if (context.state.profiles.length === 0) {
        await context.dispatch('loadProfiles');
      }
      const { profiles } = context.state;
      const userData = await API.loadUserData(); // TODO: schauen, ob userData überhaupt nötig ist? weiter unten kann auch auf profiles zugegriffen werden?
      context.commit('SET_ALL_USERDATA', userData);
      context.commit('SET_LANGUAGE', userData.language);
      const storageState = new StorageState();
      // On site, correct profileId is already coded in token
      if (storageState.getProfileIdInOnSiteStep()) {
        context.dispatch(
          'setProfileOnSite',
          storageState.getProfileIdInOnSiteStep()
        );
        return;
      }
      // Not onSite:
      const stopAtProfileChooser =
        (profiles.length === 1 && profiles[0].isFinished) ||
        (profiles.length === 1 && profiles[0].isLocked) ||
        profiles.length > 1;
      debug('loadProfile: profiles %o', profiles);

      debug(`loadProfile: stopAtProfileChooser ${stopAtProfileChooser}`);

      if (stopAtProfileChooser) {
        debug(`loadProfile: numberOfProfiles ${userData.numberOfProfiles}`);
        debug(`loadProfile: profiles ${profiles}`);

        router.push({ name: 'ProfileChooser' });
      } else {
        const profileId = userData.profiles[0].prof_prof_id;
        debug(`loadProfile: setActiveProfile: `, profileId);
        context.dispatch('setActiveProfile', profileId);
      }
    },

    async loadProfiles(context) {
      debug('loadProfiles');
      const response = await API.loadProfiles();
      debug('loadProfiles response', response);
      const profiles = response.profiles.map((el) => {
        return new Profile({
          name: el.name,
          id: el.id,
          status: el.status,
          isOpen: el.isOpen,
          hasActiveComponents: el.hasActiveComponents,
          discoverReportVisible: el.discoverReportVisible,
        });
      });
      debug('loadProfiles profiles', profiles);

      context.commit('SET_PROFILES', profiles);

      const formResponse = await API.getProjectForms();
      debug('loadProfiles getProjectForms', formResponse);

      const formIdentifiersMap = new Map();
      formResponse.data.forEach((el) => {
        formIdentifiersMap.set(el.comp_comp_id, el.form_identifier);
      });

      context.commit('SET_FORMS', formIdentifiersMap);
    },

    setActiveProfile(context, profileId) {
      debug('setActiveProfile to', profileId);
      const { profiles } = context.state;
      const profile = profiles.find((prof) => {
        return prof.id === profileId;
      });
      if (!profile) {
        debug(
          `setActiveProfile: ERROR no profile found for id ${profileId}; profiles are %o`,
          profiles
        );
        // TODO Fehler? oder neu laden?
        return;
      }
      debug(`setActiveProfile ${profile}`);
      context.commit('SET_ACTIVE_PROFILE', profile);
    },

    clearActiveProfile(context) {
      debug('clearActiveProfile');
      context.commit('SET_ACTIVE_PROFILE', null);
    },

    feedbackModalVisibility(context, value) {
      debug('feedbackModalVisibility');
      context.commit('REQUEST_FEEDBACK', value);
    },

    hideFeedbackModal(context) {
      debug('hideFeedbackModal');
      const storageState = new StorageState();
      storageState.setFeedbackModalVisibility(context.state.userData.userName);
      context.commit('FEEDBACK_SEEN', true);
      context.commit('REQUEST_FEEDBACK', false);
    },

    setVideoModal(context, module) {
      debug('setVideoModal %o', module);
      context.commit('SET_VIDEO_MODULE', module);
    },

    saveAs(context, { file, name }) {
      saveAs(file, name);
      debug('saveAs', name);
    },

    saveReportStandardMode(context, { file, name, isSmartphone }) {
      if (isSmartphone === true) {
        window.open(file, '_blank');
      } else {
        saveAs(file, name);
      }
      debug('saveReportStandardMode', name);
    },

    async saveReport(context) {
      debug('saveReport');
      const profileId = context.state.activeProfile.id;
      if (!profileId) {
        context.dispatch('loadProfile');
        return;
      }
      const report = await API.fetchReport(profileId);
      if (!report) throw new Error('Report download failed.');
      saveAs(report, 'report.pdf');
    },

    async fetchReport(context) {
      debug('fetchReport');
      const profileId = context.state.activeProfile.id;
      if (!profileId) {
        context.dispatch('loadProfile');
        return;
      }
      const report = await API.fetchReport(profileId);
      if (!report) throw new Error('Report download failed.');
      const fileURL = window.URL.createObjectURL(report);
      context.commit('SET_REPORT', fileURL);
    },

    async sendFeedback(context, feedback) {
      debug('sendFeedback', feedback);
      await API.sendFeedback(feedback);
      context.commit('REQUEST_FEEDBACK', false);
      context.dispatch('fetchData');
    },

    async finalizeApplication(context) {
      debug('finalizeApplication');
      await API.finalizeApplication();
      context.dispatch('fetchData');
    },

    registerUser(context, options) {
      debug('registerUser');
      const opts = {
        language: context.state.language,
        dataProtectionStatementId: context.state.dataProtectionStatement.id,
        dataProtectionStatementRevision:
          context.state.dataProtectionStatement.revision,
        ...options,
      };
      return API.registerUser(opts);
    },
    async registerAnon(context, options) {
      debug('registerAnon');
      const opts = {
        language: context.state.language,
        dataProtectionStatementId: context.state.dataProtectionStatement.id,
        dataProtectionStatementRevision:
          context.state.dataProtectionStatement.revision,
        profileId: router.currentRoute.query.profileId || '',
        ...options,
      };
      const data = await API.registerAnon(opts);
      const storageState = new StorageState();
      storageState.setToken(data.access_token);
      storageState.setExpiresIn(data.expires_in);
      router.push({ name: data.expired ? 'PageNotFound' : 'Home' });
      context.commit('SET_LOGGED_IN', true);
    },

    updatePassword(context, options) {
      debug('updatePassword');
      if (!context.state.config.enableRegistration) return false;

      const opts = {
        language: context.state.language,
        context: 'user',
        ...options,
      };
      return API.updatePassword(opts);
    },

    async login(context, { payload }) {
      debug('login');
      if (!context.state.config) {
        setTimeout(() => context.dispatch('login', { payload }), 100);
      }
      try {
        const pl = {
          language: context.state.language,
          dataProtectionStatementId: context.state.dataProtectionStatement.id,
          dataProtectionStatementRevision:
            context.state.dataProtectionStatement.revision,
          ...payload,
        };
        const data = await API.loginUser({
          payload: pl,
        });
        const storageState = new StorageState();
        storageState.setToken(data.access_token);
        storageState.setExpiresIn(data.expires_in);
        router.push({ name: data.expired ? 'PageNotFound' : 'Home' });
        context.commit('SET_LOGGED_IN', true);
        return { status: 'success' };
      } catch (error) {
        context.dispatch('setResponse', error);
        return { status: 'failed', error };
      }
    },

    async onSiteLogin(context, { payload }) {
      debug('onSiteLogin');
      if (!context.state.config) {
        setTimeout(() => context.dispatch('onSiteLogin', { payload }), 100);
      }
      try {
        const pl = {
          language: context.state.language,
          dataProtectionStatementId: context.state.dataProtectionStatement.id,
          dataProtectionStatementRevision:
            context.state.dataProtectionStatement.revision,
          profileId: router.currentRoute.query.profileId,
          ...payload,
        };
        const data = await API.loginOnSiteUser({
          payload: pl,
        });
        const storageState = new StorageState();
        storageState.setToken(data.access_token);
        storageState.setExpiresIn(data.expires_in);
        context.commit('SET_ON_SITE', true);
        router.push({ name: data.expired ? 'PageNotFound' : 'Home' });
        context.commit('SET_LOGGED_IN', true);
        return { status: 'success' };
      } catch (error) {
        context.dispatch('setResponse', error);
        return { status: 'failed', error };
      }
    },

    async resetPassword(context, options) {
      debug('resetPassword');
      const opts = {
        language: context.state.language,
        context: 'user',
        ...options,
      };
      await API.resetPassword(opts);
    },

    async resetPasswordByUserName(context, options) {
      debug('resetPasswordByUserName');
      try {
        const opts = {
          language: context.state.language,
          context: 'user',
          ...options,
        };
        const response = await API.resetPasswordByUserName(opts);
        return { status: 'success', message: response.message };
      } catch (error) {
        return { status: 'failed', message: error };
      }
    },

    async getPasswordRequirementsByUserHash(context, options) {
      debug('getPasswordRequirementsByUserHash, options: %o', options);

      const response = await API.getPasswordRequirementsByUserHash(options);
      debug('getPasswordRequirementsByUserHash, response: %o', response);

      context.commit('SET_PASSWORT_REQUIREMENTS', response);
      debug('context- commited');
    },
    async getPasswordRequirementsByUserId(context) {
      debug(
        'getPasswordRequirementsByUserId: userId ',
        context.state.userData.userId
      );

      const response = await API.getPasswordRequirementsByUserId();
      debug('getPasswordRequirementsByUserId, response: %o', response);

      context.commit('SET_PASSWORT_REQUIREMENTS', response);
    },

    async getPasswordRequirementsForSelfRegister(context, options) {
      debug('getPasswordRequirementsForSelfRegister, options: %o', options);

      const response = await API.getPasswordRequirementsForSelfRegister(
        options
      );
      debug('getPasswordRequirementsForSelfRegister, response: %o', response);

      context.commit('SET_PASSWORT_REQUIREMENTS', response);
      debug('context- commited');
    },

    async saveNewPassword(context, options) {
      debug('saveNewPassword');
      try {
        const opts = {
          language: context.state.language,
          context: 'user',
          ...options,
        };
        await API.saveNewPassword(opts);
        return { status: 'success' };
      } catch (error) {
        return { status: 'failed' };
      }
    },

    async changePassword(context, options) {
      debug('changePassword');
      try {
        const { status } = await API.changePassword(options);
        return { status };
      } catch (e) {
        const errorMessage =
          e && e.response && e.response.data && e.response.data.error;
        return { status: 'failure', message: errorMessage };
      }
    },

    async accountActivation(context, userIdHash) {
      debug('accountActivation', userIdHash);
      try {
        await API.accountActivation(userIdHash);
      } catch (error) {
        if (error && error.response && error.response.status === 403) {
          console.log('Account already activated.');
        } else {
          throw error;
        }
      }
    },

    addVideos(context, videos) {
      debug('addVideos', videos);
      if (!(videos instanceof Array)) {
        throw new Error('addVideos expects and Array');
      }
      context.commit('ADD_VIDEOS', videos);
    },
    /**
     * setze isLoggedIn abhängig vom Token; nötig für Reload-Fall, wenn der State leer geräumt wird
     * TODO: wäre besser, wenn onReload einfach eine initFunktion läuft, die den State abhängig vom token setzt
     */
    checkLoggedIn(context) {
      const storageState = new StorageState();
      if (!storageState.isLoggedIn()) {
        if (context.state.isLoggedIn) {
          context.commit('SET_LOGGED_IN', false);
        }
      } else if (!context.state.isLoggedIn) {
        context.commit('SET_LOGGED_IN', true);
      }
    },

    async logout(context) {
      debug('logout');
      const res = await API.logout();
      if (res.status === 'OK') {
        localStorage.removeItem('token');
        context.commit('SET_LOGGED_IN', false);
        const { isOnSite, config, activeProfile } = context.state;
        if (isOnSite) {
          router.push({
            name: 'Login',
            query: { 'on-site': true, profileId: activeProfile.id },
          });
          return;
        }
        if (config.enableAnonRegistration) {
          router.push({
            name: 'Registration',
            query: { profileId: activeProfile.id },
          });
          return;
        }
        router.push({ name: 'Login' });
        // einfach nur zur Login-Route wechseln reicht nicht, weil das CSRF-Token wegen des Logouts auch erneuert werden muss
        // eslint-disable-next-line
        location.reload();
      }
    },

    disableVideoArchive(context) {
      debug('disableVideoArchive');
      context.commit('SET_SHOW_VIDEO_ARCHIVE', false);
    },

    setTexts(context, texts) {
      context.commit('SET_TEXTS', texts);
    },
    enableVideoArchive(context) {
      debug('enableVideoArchive');
      context.commit('SET_SHOW_VIDEO_ARCHIVE', true);
    },

    async loadReportBindings(context) {
      debug('loadReportBindings');
      const profileId = context.state.activeProfile.id;
      if (!profileId) {
        context.dispatch('loadProfile');
        return;
      }
      const reportBindings = await API.fetchReportBindings(profileId);
      if (!reportBindings) throw new Error("Couldn't load report details.");
      else {
        debug('loadReportBindings: ', reportBindings);
        context.commit(
          'SET_REPORT_BINDINGS',
          reportBindings.relevantReportDefinitionBindings[0]
        );
      }
    },
    trackPicatNavigation(context) {
      const profileId = context.state.activeProfile.id;
      if (!profileId) {
        context.dispatch('loadProfile');
        return;
      }
      return API.trackPicatNavigation(profileId); /*eslint-disable-line */
    },
    trackFormAction(context) {
      const profileId = context.state.activeProfile.id;
      if (!profileId) {
        context.dispatch('loadProfile');
        return;
      }
      return API.trackFormAction(profileId); /*eslint-disable-line */
    },
    trackVideoAction(context) {
      const profileId = context.state.activeProfile.id;
      if (!profileId) {
        context.dispatch('loadProfile');
        return;
      }
      return API.trackVideoAction(profileId); /*eslint-disable-line */
    },
  };

  return actions;
};
